import { Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { 
  FileText, Receipt, FileCheck, Clock, AlertTriangle,
  CheckCircle, DollarSign, Loader2, Bell,
  TrendingUp, TrendingDown, Plus, Package, ArrowRight,
  Wallet, BarChart3, ShoppingCart,
  Activity, Zap, ArrowUpRight, ArrowDownRight,
  Sparkles, RefreshCw, FileBarChart, Download,
  Users, PieChart as PieChartIcon, Target
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import html2pdf from "html2pdf.js";
import { 
  LineChart, Line, AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  BarChart, Bar
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const paymentMethods = ["Cash", "Bank Transfer", "Mobile Money", "Card", "Check", "Other"];
const paymentDestinations = [
  { value: "cash", label: "Cash at Office" },
  { value: "bank", label: "Bank Account" },
  { value: "mobile_money", label: "Mobile Money" },
];

const COLORS = ['#059669', '#f59e0b', '#f97316', '#ef4444', '#dc2626'];

export default function Home() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [businessProfile, setBusinessProfile] = useState(null);
  const [invoiceData, setInvoiceData] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportContent, setReportContent] = useState("");
  
  // Payment dialog state
  const [showPayDialog, setShowPayDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    paid_date: new Date().toISOString().split("T")[0],
    payment_method: "Bank Transfer",
    payment_destination: "bank",
    paid_amount: ""
  });
  const [processing, setProcessing] = useState(false);
  const reportRef = useRef(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const [dashRes, profileRes, invoicesRes] = await Promise.all([
        axios.get(`${API}/dashboard/summary`),
        axios.get(`${API}/business-profile`),
        axios.get(`${API}/invoices/pending`)
      ]);
      setDashboardData(dashRes.data);
      setBusinessProfile(profileRes.data);
      setInvoiceData(invoicesRes.data);
    } catch (error) {
      console.error("Failed to load dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadAiInsights = async () => {
    setLoadingInsights(true);
    try {
      const response = await axios.get(`${API}/dashboard/ai-insights`);
      setAiInsights(response.data);
    } catch (error) {
      toast.error("Failed to generate insights");
    } finally {
      setLoadingInsights(false);
    }
  };

  const generateReport = async () => {
    setGeneratingReport(true);
    try {
      const response = await axios.post(`${API}/dashboard/generate-report`);
      setReportContent(response.data);
      setShowReportDialog(true);
    } catch (error) {
      toast.error("Failed to generate report");
    } finally {
      setGeneratingReport(false);
    }
  };

  const downloadReport = async () => {
    if (!reportRef.current) return;
    
    const businessName = reportContent?.business_name || "Business";
    const reportDate = new Date().toISOString().split("T")[0];
    
    toast.info("Generating PDF...");
    
    const opt = {
      margin: [10, 10, 10, 10],
      filename: `${businessName.replace(/\s+/g, "_")}_Financial_Report_${reportDate}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
    
    try {
      await html2pdf().set(opt).from(reportRef.current).save();
      toast.success("PDF downloaded successfully");
    } catch (error) {
      toast.error("Failed to generate PDF");
    }
  };

  const formatCurrency = (amount, currency = "USD") => {
    if (amount === null || amount === undefined) return "$0";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  };

  const openPayDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentForm({
      paid_date: new Date().toISOString().split("T")[0],
      payment_method: "Bank Transfer",
      payment_destination: "bank",
      paid_amount: invoice.amount?.toString() || ""
    });
    setShowPayDialog(true);
  };

  const handleMarkPaid = async () => {
    if (!selectedInvoice) return;
    setProcessing(true);
    try {
      await axios.post(`${API}/documents/create-receipt-from-invoice`, {
        invoice_id: selectedInvoice.id,
        paid_date: paymentForm.paid_date,
        payment_method: paymentForm.payment_method,
        payment_destination: paymentForm.payment_destination,
        paid_amount: parseFloat(paymentForm.paid_amount) || selectedInvoice.amount
      });
      toast.success("Payment recorded!");
      setShowPayDialog(false);
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record payment");
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  const data = dashboardData || { kpis: {}, smart_actions: [], recent_activity: [], cash_trend: [], receivables_aging: {}, advanced_analytics: {} };
  const kpis = data.kpis || {};
  const analytics = data.advanced_analytics || {};
  const allInvoices = invoiceData ? [...(invoiceData.overdue || []), ...(invoiceData.pending || [])] : [];
  
  // Admin check - only owners/admins can generate reports
  const isAdmin = user?.role === 'owner' || user?.role === 'admin' || !user?.company_id;

  // Helper to get risk color
  const getRiskColor = (level) => {
    switch(level) {
      case 'low': return 'text-emerald-600 bg-emerald-50';
      case 'medium': return 'text-amber-600 bg-amber-50';
      case 'high': return 'text-orange-600 bg-orange-50';
      case 'critical': return 'text-red-600 bg-red-50';
      default: return 'text-slate-600 bg-slate-50';
    }
  };

  return (
    <div className="space-y-6" data-testid="home-page">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-200 pb-4">
        <div className="flex items-center gap-3">
          {(businessProfile?.logo_data || businessProfile?.logo_url) && (
            <img 
              src={businessProfile.logo_data || businessProfile.logo_url} 
              alt={businessProfile?.business_name || "Company"} 
              className="h-10 w-auto max-w-[100px] object-contain"
              data-testid="company-logo"
            />
          )}
          <div>
            <h1 className="text-lg font-semibold text-slate-800">
              {businessProfile?.business_name ? `${getGreeting()}, ${businessProfile.business_name}` : getGreeting()}
            </h1>
            <p className="text-sm text-slate-500">
              {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/dashboard/tools/payment-invoice">
            <Button size="sm" className="h-8">
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              New Invoice
            </Button>
          </Link>
        </div>
      </div>

      {/* KPI Strip - Clean inline design */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-4 py-2" data-testid="kpi-strip">
        <div>
          <p className="text-xs text-slate-500 flex items-center gap-1">
            <Wallet className="w-3 h-3" /> Cash
          </p>
          <p className="text-lg font-bold text-slate-800">{formatCurrency(kpis.cash_balance)}</p>
        </div>
        <div>
          <p className="text-xs text-slate-500 flex items-center gap-1">
            <TrendingUp className="w-3 h-3" /> Net (Month)
          </p>
          <p className={`text-lg font-bold ${kpis.net_this_month >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {kpis.net_this_month >= 0 ? '+' : ''}{formatCurrency(kpis.net_this_month)}
          </p>
          {kpis.net_change_pct !== 0 && (
            <p className={`text-xs ${kpis.net_change_pct >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
              {kpis.net_change_pct >= 0 ? '↑' : '↓'} {Math.abs(kpis.net_change_pct)}%
            </p>
          )}
        </div>
        <div>
          <p className="text-xs text-slate-500 flex items-center gap-1">
            <FileText className="w-3 h-3" /> Outstanding
          </p>
          <p className="text-lg font-bold text-amber-600">{formatCurrency(kpis.total_outstanding)}</p>
          {kpis.overdue_count > 0 && <p className="text-xs text-red-500">{kpis.overdue_count} overdue</p>}
        </div>
        <div>
          <p className="text-xs text-slate-500 flex items-center gap-1">
            <BarChart3 className="w-3 h-3" /> Margin
          </p>
          <p className="text-lg font-bold text-slate-800">{kpis.profit_margin || 0}%</p>
        </div>
        <div>
          <p className="text-xs text-slate-500 flex items-center gap-1">
            <DollarSign className="w-3 h-3" /> Revenue
          </p>
          <p className="text-lg font-bold text-emerald-600">{formatCurrency(kpis.total_revenue)}</p>
        </div>
        <div>
          <p className="text-xs text-slate-500 flex items-center gap-1">
            <Package className="w-3 h-3" /> Inventory
          </p>
          <p className="text-lg font-bold text-slate-800">{formatCurrency(kpis.inventory_value)}</p>
          {kpis.low_stock_count > 0 && <p className="text-xs text-amber-600">{kpis.low_stock_count} low</p>}
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* Smart Actions */}
          {data.smart_actions && data.smart_actions.length > 0 && (
            <div data-testid="smart-actions">
              <h2 className="text-sm font-medium text-slate-600 mb-2 flex items-center gap-1.5">
                <Zap className="w-3.5 h-3.5 text-amber-500" />
                Action Required
              </h2>
              <div className="space-y-1.5">
                {data.smart_actions.map((action, idx) => (
                  <Link
                    key={idx}
                    to={action.action_url}
                    className={`flex items-center justify-between py-2 px-3 border-l-2 bg-slate-50 hover:bg-slate-100 transition-colors ${
                      action.priority === 'high' ? 'border-l-red-500' : 
                      action.priority === 'medium' ? 'border-l-amber-500' : 'border-l-blue-500'
                    }`}
                    data-testid={`action-${action.type}`}
                  >
                    <div className="flex items-center gap-2">
                      {action.type === 'overdue_invoices' && <AlertTriangle className="w-4 h-4 text-red-500" />}
                      {action.type === 'low_stock' && <Package className="w-4 h-4 text-amber-500" />}
                      {action.type === 'cash_movement' && <Activity className="w-4 h-4 text-blue-500" />}
                      <div>
                        <p className="text-sm font-medium text-slate-800">{action.title}</p>
                        <p className="text-xs text-slate-500">{action.description}</p>
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-400" />
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* AI Insights Panel */}
          <div data-testid="ai-insights-panel" className="border border-slate-200 bg-gradient-to-br from-slate-50 to-white">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />
                <h2 className="text-sm font-semibold text-slate-800">AI Insights</h2>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-7 text-xs"
                  onClick={loadAiInsights}
                  disabled={loadingInsights}
                >
                  {loadingInsights ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                  {loadingInsights ? 'Analyzing...' : 'Refresh'}
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="h-7 text-xs"
                  onClick={generateReport}
                  disabled={generatingReport}
                >
                  {generatingReport ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <FileBarChart className="w-3 h-3 mr-1" />}
                  {generatingReport ? 'Generating...' : 'Full Report'}
                </Button>
              </div>
            </div>
            
            {!aiInsights && !loadingInsights && (
              <div className="p-6 text-center">
                <Sparkles className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500 mb-3">Get AI-powered insights on your financial data</p>
                <Button size="sm" onClick={loadAiInsights}>
                  Generate Insights
                </Button>
              </div>
            )}
            
            {loadingInsights && (
              <div className="p-6 text-center">
                <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto mb-2" />
                <p className="text-sm text-slate-500">Analyzing your financial data...</p>
              </div>
            )}
            
            {aiInsights && !loadingInsights && (
              <div className="p-4 space-y-4">
                {/* Summary */}
                <p className="text-sm text-slate-700 font-medium">{aiInsights.summary}</p>
                
                {/* Insights */}
                <div className="space-y-2">
                  {aiInsights.insights?.map((insight, idx) => (
                    <div 
                      key={idx} 
                      className={`p-3 border-l-2 ${
                        insight.type === 'positive' ? 'border-l-emerald-500 bg-emerald-50/50' :
                        insight.type === 'critical' ? 'border-l-red-500 bg-red-50/50' :
                        insight.type === 'warning' ? 'border-l-amber-500 bg-amber-50/50' :
                        'border-l-blue-500 bg-blue-50/50'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-sm font-medium text-slate-800">{insight.title}</p>
                          <p className="text-xs text-slate-600 mt-0.5">{insight.description}</p>
                        </div>
                        <span className={`text-xs font-bold px-2 py-0.5 ${
                          insight.type === 'positive' ? 'text-emerald-700 bg-emerald-100' :
                          insight.type === 'critical' ? 'text-red-700 bg-red-100' :
                          insight.type === 'warning' ? 'text-amber-700 bg-amber-100' :
                          'text-blue-700 bg-blue-100'
                        }`}>
                          {insight.metric}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Recommendation */}
                {aiInsights.recommendation && (
                  <div className="pt-3 border-t border-slate-100">
                    <p className="text-xs text-slate-500 mb-1">Recommendation</p>
                    <p className="text-sm text-slate-700">{aiInsights.recommendation}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Invoices to Collect */}
          {allInvoices.length > 0 && (
            <div data-testid="invoices-section">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-sm font-medium text-slate-600">Invoices to Collect</h2>
                <Link to="/dashboard/accounts-receivable" className="text-xs text-primary hover:underline">View all →</Link>
              </div>
              <table className="w-full text-sm border border-slate-200">
                <tbody className="divide-y divide-slate-100">
                  {allInvoices.slice(0, 5).map((invoice) => (
                    <tr key={invoice.id} className={`hover:bg-slate-50 ${invoice.computed_status === 'overdue' ? 'bg-red-50/30' : ''}`}>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          {invoice.computed_status === 'overdue' ? (
                            <AlertTriangle className="w-3.5 h-3.5 text-red-500" />
                          ) : (
                            <Clock className="w-3.5 h-3.5 text-amber-500" />
                          )}
                          <div>
                            <p className="font-medium text-slate-800">{invoice.doc_number || 'Invoice'}</p>
                            <p className="text-xs text-slate-500">{invoice.client_name || 'No client'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <p className={`font-semibold ${invoice.computed_status === 'overdue' ? 'text-red-600' : 'text-slate-800'}`}>
                          {formatCurrency(invoice.amount, invoice.currency)}
                        </p>
                        <p className="text-xs text-slate-500">
                          {invoice.computed_status === 'overdue' ? `${invoice.days_overdue}d overdue` : 
                           invoice.days_until_due === 0 ? 'Due today' : `Due in ${invoice.days_until_due}d`}
                        </p>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openPayDialog(invoice)}>
                          Mark paid
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Analytics Charts Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Cash Flow Area Chart */}
            {data.cash_trend && data.cash_trend.length > 0 && (
              <div data-testid="cash-trend" className="border border-slate-200 p-4">
                <h2 className="text-sm font-medium text-slate-600 mb-3">Cash Flow Trend</h2>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data.cash_trend} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                      <defs>
                        <linearGradient id="colorIn" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#059669" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#059669" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorOut" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} />
                      <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={(v) => `$${v >= 1000 ? (v/1000).toFixed(0) + 'k' : v}`} />
                      <Tooltip 
                        formatter={(value) => [`$${value.toLocaleString()}`, '']}
                        contentStyle={{ fontSize: 12, borderRadius: 4, border: '1px solid #e2e8f0' }}
                      />
                      <Area type="monotone" dataKey="inflows" stroke="#059669" strokeWidth={2} fill="url(#colorIn)" name="Inflows" />
                      <Area type="monotone" dataKey="outflows" stroke="#ef4444" strokeWidth={2} fill="url(#colorOut)" name="Outflows" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex items-center justify-center gap-6 mt-2 text-xs text-slate-500">
                  <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-emerald-600" /> Inflows</span>
                  <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-red-500" /> Outflows</span>
                </div>
              </div>
            )}

            {/* Receivables Aging Pie Chart */}
            <div data-testid="receivables-aging" className="border border-slate-200 p-4">
              <h2 className="text-sm font-medium text-slate-600 mb-3">Receivables Aging</h2>
              <div className="h-48">
                {data.receivables_aging && kpis.total_outstanding > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Current', value: data.receivables_aging.current || 0 },
                          { name: '1-30 days', value: data.receivables_aging.days_0_30 || 0 },
                          { name: '31-60 days', value: data.receivables_aging.days_31_60 || 0 },
                          { name: '61-90 days', value: data.receivables_aging.days_61_90 || 0 },
                          { name: '90+ days', value: data.receivables_aging.days_90_plus || 0 },
                        ].filter(d => d.value > 0)}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={70}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {[
                          { name: 'Current', value: data.receivables_aging.current || 0 },
                          { name: '1-30 days', value: data.receivables_aging.days_0_30 || 0 },
                          { name: '31-60 days', value: data.receivables_aging.days_31_60 || 0 },
                          { name: '61-90 days', value: data.receivables_aging.days_61_90 || 0 },
                          { name: '90+ days', value: data.receivables_aging.days_90_plus || 0 },
                        ].filter(d => d.value > 0).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, '']} contentStyle={{ fontSize: 12, borderRadius: 4 }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full text-slate-400 text-sm">
                    No outstanding receivables
                  </div>
                )}
              </div>
              <div className="flex flex-wrap justify-center gap-3 mt-1 text-xs text-slate-500">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-600" /> Current</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> 1-30d</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-500" /> 31-60d</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> 61-90d</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-700" /> 90d+</span>
              </div>
            </div>
          </div>

          {/* Net Cash Flow Line Chart */}
          {data.cash_trend && data.cash_trend.length > 0 && (
            <div data-testid="net-cash-chart" className="border border-slate-200 p-4">
              <h2 className="text-sm font-medium text-slate-600 mb-3">Net Cash Flow (7 Days)</h2>
              <div className="h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.cash_trend} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={(v) => `$${v >= 1000 ? (v/1000).toFixed(0) + 'k' : v}`} />
                    <Tooltip 
                      formatter={(value) => [`$${value.toLocaleString()}`, 'Net']}
                      contentStyle={{ fontSize: 12, borderRadius: 4, border: '1px solid #e2e8f0' }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="net" 
                      stroke="#0369a1" 
                      strokeWidth={2} 
                      dot={{ fill: '#0369a1', strokeWidth: 0, r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          
          {/* Quick Actions */}
          <div data-testid="quick-actions">
            <h2 className="text-sm font-medium text-slate-600 mb-2">Quick Actions</h2>
            <div className="space-y-1">
              <Link to="/dashboard/tools/payment-invoice">
                <Button variant="ghost" className="w-full justify-start h-9 text-sm font-normal hover:bg-slate-100">
                  <FileText className="w-4 h-4 mr-2 text-slate-500" />
                  Create Payment Invoice
                </Button>
              </Link>
              <Link to="/dashboard/quick-sale">
                <Button variant="ghost" className="w-full justify-start h-9 text-sm font-normal hover:bg-slate-100">
                  <ShoppingCart className="w-4 h-4 mr-2 text-slate-500" />
                  Quick Sale
                </Button>
              </Link>
              <Link to="/dashboard/tools/receipt">
                <Button variant="ghost" className="w-full justify-start h-9 text-sm font-normal hover:bg-slate-100">
                  <Receipt className="w-4 h-4 mr-2 text-slate-500" />
                  Create Receipt
                </Button>
              </Link>
              <Link to="/dashboard/cash-flow/ledger">
                <Button variant="ghost" className="w-full justify-start h-9 text-sm font-normal hover:bg-slate-100">
                  <DollarSign className="w-4 h-4 mr-2 text-slate-500" />
                  Record Transaction
                </Button>
              </Link>
              <Link to="/dashboard/tools/quote">
                <Button variant="ghost" className="w-full justify-start h-9 text-sm font-normal hover:bg-slate-100">
                  <FileCheck className="w-4 h-4 mr-2 text-slate-500" />
                  Create Quote
                </Button>
              </Link>
              <Link to="/dashboard/reports/unified">
                <Button variant="ghost" className="w-full justify-start h-9 text-sm font-normal hover:bg-slate-100">
                  <BarChart3 className="w-4 h-4 mr-2 text-slate-500" />
                  Generate Report
                </Button>
              </Link>
            </div>
          </div>

          {/* Activity Feed */}
          {data.recent_activity && data.recent_activity.length > 0 && (
            <div data-testid="activity-feed">
              <h2 className="text-sm font-medium text-slate-600 mb-2 flex items-center gap-1.5">
                <Activity className="w-3.5 h-3.5" />
                Recent Activity
              </h2>
              <div className="border border-slate-200 divide-y divide-slate-100">
                {data.recent_activity.slice(0, 8).map((item, idx) => (
                  <div key={idx} className="px-3 py-2 text-sm">
                    <div className="flex items-center justify-between">
                      <p className="text-slate-700 truncate flex-1 text-xs">{item.title}</p>
                      {item.amount && (
                        <span className={`text-xs font-medium ml-2 ${item.direction === 'in' ? 'text-emerald-600' : 'text-red-500'}`}>
                          {item.direction === 'in' ? '+' : '-'}{formatCurrency(item.amount)}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Payment Dialog */}
      <Dialog open={showPayDialog} onOpenChange={setShowPayDialog}>
        <DialogContent className="sm:max-w-md" data-testid="mark-paid-dialog">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-3">
                <p className="text-sm font-medium">{selectedInvoice.doc_number}</p>
                <p className="text-xs text-slate-500">{selectedInvoice.client_name || "No client"}</p>
                <p className="text-lg font-semibold mt-1">{formatCurrency(selectedInvoice.amount, selectedInvoice.currency)}</p>
              </div>
              <div>
                <Label className="text-xs">Payment date</Label>
                <Input type="date" value={paymentForm.paid_date} onChange={(e) => setPaymentForm({ ...paymentForm, paid_date: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Destination</Label>
                <Select value={paymentForm.payment_destination} onValueChange={(v) => setPaymentForm({ ...paymentForm, payment_destination: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {paymentDestinations.map(dest => (<SelectItem key={dest.value} value={dest.value}>{dest.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Method</Label>
                <Select value={paymentForm.payment_method} onValueChange={(v) => setPaymentForm({ ...paymentForm, payment_method: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {paymentMethods.map(method => (<SelectItem key={method} value={method}>{method}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Amount</Label>
                <Input type="number" value={paymentForm.paid_amount} onChange={(e) => setPaymentForm({ ...paymentForm, paid_amount: e.target.value })} className="mt-1" />
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setShowPayDialog(false)}>Cancel</Button>
                <Button onClick={handleMarkPaid} disabled={processing}>
                  {processing && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Confirm Payment
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Report Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto p-0" data-testid="report-dialog">
          {reportContent && (
            <div className="bg-white">
              {/* PDF Content Container */}
              <div ref={reportRef} className="bg-white">
                {/* Branded Header */}
                <div className="bg-gradient-to-r from-emerald-700 to-emerald-600 text-white p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {(businessProfile?.logo_data || businessProfile?.logo_url) && (
                        <img 
                          src={businessProfile.logo_data || businessProfile.logo_url} 
                          alt="Company Logo" 
                          className="h-12 w-auto max-w-[80px] object-contain bg-white rounded p-1"
                        />
                      )}
                      <div>
                        <h1 className="text-xl font-bold">{reportContent.business_name || 'Company'}</h1>
                        <p className="text-emerald-100 text-sm">Financial Report</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-emerald-100">Report Period</p>
                      <p className="font-semibold">{reportContent.report_period}</p>
                    </div>
                  </div>
                </div>

                {/* Key Metrics Strip */}
                <div className="grid grid-cols-4 border-b border-slate-200">
                  <div className="p-4 text-center border-r border-slate-200">
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Cash Balance</p>
                    <p className="text-xl font-bold text-emerald-600">{formatCurrency(reportContent.metrics?.cash_balance)}</p>
                  </div>
                  <div className="p-4 text-center border-r border-slate-200">
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Total Revenue</p>
                    <p className="text-xl font-bold text-slate-800">{formatCurrency(reportContent.metrics?.total_revenue)}</p>
                  </div>
                  <div className="p-4 text-center border-r border-slate-200">
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Profit Margin</p>
                    <p className="text-xl font-bold text-slate-800">{reportContent.metrics?.profit_margin || 0}%</p>
                  </div>
                  <div className="p-4 text-center">
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Outstanding</p>
                    <p className="text-xl font-bold text-amber-600">{formatCurrency(reportContent.metrics?.total_outstanding)}</p>
                  </div>
                </div>

                {/* Financial Statements */}
                <div className="p-6 space-y-6">
                  <h2 className="text-lg font-bold text-slate-800 border-b border-slate-200 pb-2">FINANCIAL STATEMENTS</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Cash Flow Statement */}
                    <div className="border border-slate-200 rounded-lg overflow-hidden">
                      <div className="bg-slate-50 px-4 py-2 border-b border-slate-200">
                        <h3 className="font-semibold text-slate-700 text-sm">Cash Flow Statement</h3>
                      </div>
                      <div className="p-4 space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-600">Opening Balance</span>
                          <span className="font-medium">{formatCurrency(reportContent.statements?.cash_flow?.opening_balance)}</span>
                        </div>
                        <div className="flex justify-between text-emerald-600">
                          <span>Cash Inflows</span>
                          <span className="font-medium">+{formatCurrency(reportContent.statements?.cash_flow?.total_inflows)}</span>
                        </div>
                        <div className="flex justify-between text-red-500">
                          <span>Cash Outflows</span>
                          <span className="font-medium">-{formatCurrency(reportContent.statements?.cash_flow?.total_outflows)}</span>
                        </div>
                        <div className="flex justify-between border-t border-slate-200 pt-2 font-bold">
                          <span>Closing Balance</span>
                          <span className="text-emerald-600">{formatCurrency(reportContent.statements?.cash_flow?.closing_balance)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Income Statement */}
                    <div className="border border-slate-200 rounded-lg overflow-hidden">
                      <div className="bg-slate-50 px-4 py-2 border-b border-slate-200">
                        <h3 className="font-semibold text-slate-700 text-sm">Income Statement</h3>
                      </div>
                      <div className="p-4 space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-600">Total Revenue</span>
                          <span className="font-medium">{formatCurrency(reportContent.statements?.income?.total_revenue)}</span>
                        </div>
                        <div className="flex justify-between text-red-500">
                          <span>Cost of Goods Sold</span>
                          <span className="font-medium">-{formatCurrency(reportContent.statements?.income?.cogs)}</span>
                        </div>
                        <div className="flex justify-between border-t border-slate-200 pt-2 font-bold">
                          <span>Gross Profit</span>
                          <span className="text-emerald-600">{formatCurrency(reportContent.statements?.income?.gross_profit)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Balance Sheet */}
                    <div className="border border-slate-200 rounded-lg overflow-hidden">
                      <div className="bg-slate-50 px-4 py-2 border-b border-slate-200">
                        <h3 className="font-semibold text-slate-700 text-sm">Balance Sheet</h3>
                      </div>
                      <div className="p-4 space-y-2 text-sm">
                        <p className="text-xs text-slate-500 uppercase">Assets</p>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Cash</span>
                          <span className="font-medium">{formatCurrency(reportContent.metrics?.cash_balance)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Receivables</span>
                          <span className="font-medium">{formatCurrency(reportContent.metrics?.total_outstanding)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Inventory</span>
                          <span className="font-medium">{formatCurrency(reportContent.metrics?.inventory_value || 0)}</span>
                        </div>
                        <div className="flex justify-between border-t border-slate-200 pt-2 font-bold">
                          <span>Total Assets</span>
                          <span>{formatCurrency((reportContent.metrics?.cash_balance || 0) + (reportContent.metrics?.total_outstanding || 0) + (reportContent.metrics?.inventory_value || 0))}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Analytics Charts */}
                  <div>
                    <h2 className="text-lg font-bold text-slate-800 border-b border-slate-200 pb-2 mb-4">FINANCIAL ANALYTICS</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Asset Distribution Chart */}
                      <div className="border border-slate-200 rounded-lg p-4">
                        <h3 className="font-semibold text-slate-700 text-sm mb-3">Asset Distribution</h3>
                        <div className="h-48">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={[
                                  { name: 'Cash', value: reportContent.metrics?.cash_balance || 0 },
                                  { name: 'Receivables', value: reportContent.metrics?.total_outstanding || 0 },
                                  { name: 'Inventory', value: reportContent.metrics?.inventory_value || 0 }
                                ].filter(d => d.value > 0)}
                                cx="50%"
                                cy="50%"
                                innerRadius={35}
                                outerRadius={65}
                                paddingAngle={2}
                                dataKey="value"
                                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                labelLine={false}
                              >
                                <Cell fill="#059669" />
                                <Cell fill="#f59e0b" />
                                <Cell fill="#6366f1" />
                              </Pie>
                              <Tooltip formatter={(value) => formatCurrency(value)} />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      </div>

                      {/* Revenue vs Expenses */}
                      <div className="border border-slate-200 rounded-lg p-4">
                        <h3 className="font-semibold text-slate-700 text-sm mb-3">Revenue vs Expenses</h3>
                        <div className="h-48">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={[
                              { name: 'Revenue', value: reportContent.statements?.income?.total_revenue || 0 },
                              { name: 'COGS', value: reportContent.statements?.income?.cogs || 0 },
                              { name: 'Profit', value: reportContent.statements?.income?.gross_profit || 0 }
                            ]}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v >= 1000 ? (v/1000).toFixed(0) + 'k' : v}`} />
                              <Tooltip formatter={(value) => formatCurrency(value)} />
                              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                                <Cell fill="#059669" />
                                <Cell fill="#ef4444" />
                                <Cell fill="#3b82f6" />
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>

                      {/* Cash Flow Chart */}
                      <div className="border border-slate-200 rounded-lg p-4">
                        <h3 className="font-semibold text-slate-700 text-sm mb-3">Cash Flow Analysis</h3>
                        <div className="h-48">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={[
                              { name: 'Opening', value: reportContent.statements?.cash_flow?.opening_balance || 0 },
                              { name: 'Inflows', value: reportContent.statements?.cash_flow?.total_inflows || 0 },
                              { name: 'Outflows', value: -(reportContent.statements?.cash_flow?.total_outflows || 0) },
                              { name: 'Closing', value: reportContent.statements?.cash_flow?.closing_balance || 0 }
                            ]}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${Math.abs(v) >= 1000 ? (v/1000).toFixed(0) + 'k' : v}`} />
                              <Tooltip formatter={(value) => formatCurrency(Math.abs(value))} />
                              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                                <Cell fill="#6366f1" />
                                <Cell fill="#059669" />
                                <Cell fill="#ef4444" />
                                <Cell fill="#3b82f6" />
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>

                      {/* Key Ratios */}
                      <div className="border border-slate-200 rounded-lg p-4">
                        <h3 className="font-semibold text-slate-700 text-sm mb-3">Key Financial Ratios</h3>
                        <div className="space-y-3">
                          <div>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-slate-600">Profit Margin</span>
                              <span className="font-medium">{reportContent.metrics?.profit_margin || 0}%</span>
                            </div>
                            <div className="w-full bg-slate-200 rounded-full h-2">
                              <div className="bg-emerald-500 h-2 rounded-full" style={{ width: `${Math.min(reportContent.metrics?.profit_margin || 0, 100)}%` }}></div>
                            </div>
                          </div>
                          <div>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-slate-600">Collection Rate</span>
                              <span className="font-medium">
                                {reportContent.metrics?.total_revenue > 0 
                                  ? Math.round(((reportContent.metrics?.total_revenue - reportContent.metrics?.total_outstanding) / reportContent.metrics?.total_revenue) * 100)
                                  : 0}%
                              </span>
                            </div>
                            <div className="w-full bg-slate-200 rounded-full h-2">
                              <div className="bg-blue-500 h-2 rounded-full" style={{ 
                                width: `${reportContent.metrics?.total_revenue > 0 
                                  ? Math.min(((reportContent.metrics?.total_revenue - reportContent.metrics?.total_outstanding) / reportContent.metrics?.total_revenue) * 100, 100)
                                  : 0}%` 
                              }}></div>
                            </div>
                          </div>
                          <div>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-slate-600">Cash Ratio</span>
                              <span className="font-medium">
                                {reportContent.metrics?.total_outstanding > 0 
                                  ? (reportContent.metrics?.cash_balance / reportContent.metrics?.total_outstanding).toFixed(2)
                                  : 'N/A'}
                              </span>
                            </div>
                            <div className="w-full bg-slate-200 rounded-full h-2">
                              <div className="bg-amber-500 h-2 rounded-full" style={{ 
                                width: `${reportContent.metrics?.total_outstanding > 0 
                                  ? Math.min((reportContent.metrics?.cash_balance / reportContent.metrics?.total_outstanding) * 50, 100)
                                  : 0}%` 
                              }}></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* AI Analysis */}
                  <div>
                    <h2 className="text-lg font-bold text-slate-800 border-b border-slate-200 pb-2 mb-4">ANALYSIS & RECOMMENDATIONS</h2>
                    <div className="text-sm text-slate-700 leading-relaxed bg-slate-50 p-5 rounded-lg">
                      {reportContent.report?.split('\n').map((line, idx) => {
                        const trimmed = line.trim();
                        if (!trimmed) return <br key={idx} />;
                        if (trimmed === trimmed.toUpperCase() && trimmed.length > 3 && !trimmed.includes('$')) {
                          return <h3 key={idx} className="font-bold text-slate-800 mt-4 mb-2 text-base">{trimmed}</h3>;
                        }
                        return <p key={idx} className="mb-2">{trimmed}</p>;
                      })}
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="border-t border-slate-200 pt-4 flex justify-between items-center text-xs text-slate-400">
                    <div>
                      <p>Generated on {new Date(reportContent.generated_at).toLocaleString()}</p>
                      <p>Confidential - For Internal Use Only</p>
                    </div>
                    <div className="text-right">
                      <p>{reportContent.business_name}</p>
                      <p>Powered by LedgerAMT</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="border-t border-slate-200 p-4 bg-slate-50 flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowReportDialog(false)}>Close</Button>
                <Button onClick={downloadReport} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700">
                  <Download className="w-4 h-4" />
                  Download PDF
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
